import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Compass, MapPin, Crosshair, Smartphone, RefreshCw } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/lib/store";
import { buildSeo } from "@/lib/seo";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { BD_CITIES } from "@/data/bdCities";
import {
  qiblaFor,
  qiblaBearing,
  cardinalFromBearing,
  BD_CITY_QIBLA,
  KAABA_LAT,
  KAABA_LON,
} from "@/lib/calculators/qibla";

type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "Qibla Direction", bn: "কিবলা দিক" },
  desc: {
    en: "True-north bearing from your location to the Kaaba. Works offline, with live magnetometer compass support on mobile.",
    bn: "আপনার অবস্থান থেকে কাবার সঠিক দিক (ট্রু-নর্থ বেয়ারিং)। অফলাইনে কাজ করে, মোবাইলে লাইভ কম্পাস সাপোর্ট।",
  },
  inputMode: { en: "Choose location", bn: "অবস্থান বাছাই" },
  modeAuto: { en: "Auto-detect (GPS)", bn: "অটো-ডিটেক্ট (GPS)" },
  modeCity: { en: "Pick BD city", bn: "জেলা বাছাই" },
  modeManual: { en: "Manual coordinates", bn: "ম্যানুয়াল কোঅর্ডিনেট" },
  detect: { en: "Detect my location", bn: "আমার অবস্থান নিন" },
  detecting: { en: "Detecting…", bn: "খোঁজা হচ্ছে…" },
  city: { en: "City / district", bn: "শহর / জেলা" },
  lat: { en: "Latitude", bn: "অক্ষাংশ" },
  lon: { en: "Longitude", bn: "দ্রাঘিমাংশ" },
  bearing: { en: "Qibla bearing", bn: "কিবলা বেয়ারিং" },
  distance: { en: "Distance to Kaaba", bn: "কাবা পর্যন্ত দূরত্ব" },
  liveCompass: { en: "Live compass (mobile)", bn: "লাইভ কম্পাস (মোবাইল)" },
  liveOn: { en: "Stop live compass", bn: "লাইভ কম্পাস বন্ধ" },
  liveOff: { en: "Enable live compass", bn: "লাইভ কম্পাস চালু" },
  hint: {
    en: "Point the top of your phone where the red arrow points.",
    bn: "মোবাইলের উপরের দিক লাল তীরের দিকে রাখুন।",
  },
  fromYourLocation: {
    en: "From your location, the Qibla is",
    bn: "আপনার অবস্থান থেকে কিবলা",
  },
  saveLoc: { en: "Save my location", bn: "আমার অবস্থান সেভ করুন" },
  saved: { en: "Saved ✓", bn: "সেভ হয়েছে ✓" },
  permDenied: {
    en: "Location permission denied — using Dhaka as fallback.",
    bn: "লোকেশন অনুমতি দেওয়া হয়নি — ঢাকা ডিফল্ট হিসাবে ব্যবহৃত হচ্ছে।",
  },
  noOrient: {
    en: "Magnetometer not available on this device. Showing static compass.",
    bn: "এই ডিভাইসে ম্যাগনেটোমিটার নেই। স্ট্যাটিক কম্পাস দেখানো হচ্ছে।",
  },
  precomputedTitle: {
    en: "Qibla bearing for all 64 BD districts",
    bn: "৬৪ জেলার কিবলা বেয়ারিং",
  },
};

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "Which direction is the Qibla from Bangladesh?", bn: "বাংলাদেশ থেকে কিবলা কোন দিকে?" },
    a: {
      en: "From every BD district the Qibla is roughly west, slightly tilted north — bearing about 276°–279° from true north.",
      bn: "বাংলাদেশের প্রতিটি জেলা থেকে কিবলা মোটামুটি পশ্চিম দিকে, সামান্য উত্তরে — ট্রু-নর্থ থেকে ২৭৬°–২৭৯° ডিগ্রি।",
    },
  },
  {
    q: { en: "What is the Qibla bearing from Dhaka?", bn: "ঢাকা থেকে কিবলার দিক কত ডিগ্রি?" },
    a: {
      en: "About 277° from true north (just north of due west). Distance to the Kaaba is approximately 4,620 km.",
      bn: "ট্রু-নর্থ থেকে প্রায় ২৭৭° (পশ্চিমের সামান্য উত্তরে)। কাবা পর্যন্ত দূরত্ব প্রায় ৪,৬২০ কিমি।",
    },
  },
  {
    q: { en: "How accurate is the phone compass?", bn: "মোবাইলের কম্পাস কতটা সঠিক?" },
    a: {
      en: "Smartphone magnetometers are usually within 5–15° accuracy. Calibrate by waving the phone in a figure-8 motion and stay away from metal objects, magnets and electronics.",
      bn: "স্মার্টফোনের কম্পাস সাধারণত ৫–১৫° এর মধ্যে সঠিক। ফোনটি 8-আকৃতিতে ঘুরিয়ে ক্যালিব্রেট করুন এবং ধাতব বস্তু, চুম্বক ও ইলেকট্রনিক্স থেকে দূরে থাকুন।",
    },
  },
  {
    q: { en: "Magnetic north vs true north — which do you use?", bn: "ম্যাগনেটিক নর্থ ও ট্রু-নর্থের পার্থক্য?" },
    a: {
      en: "We compute the true-north great-circle bearing. In Bangladesh magnetic declination is small (≈0–1°), so the difference is negligible for prayer.",
      bn: "আমরা ট্রু-নর্থ ভিত্তিক বেয়ারিং দিই। বাংলাদেশে ম্যাগনেটিক ডিক্লিনেশন প্রায় ০–১°, তাই নামাজের জন্য পার্থক্য নগণ্য।",
    },
  },
  {
    q: { en: "How is Qibla calculated?", bn: "কিবলা কীভাবে নির্ণয় করা হয়?" },
    a: {
      en: "Using the great-circle initial bearing formula on the WGS-84 sphere from your latitude/longitude to the Kaaba (21.4225°N, 39.8262°E).",
      bn: "WGS-84 গোলকের উপর গ্রেট-সার্কেল ইনিশিয়াল বেয়ারিং সূত্র দিয়ে — আপনার অক্ষাংশ-দ্রাঘিমাংশ থেকে কাবা (২১.৪২২৫°উ, ৩৯.৮২৬২°পূ) পর্যন্ত।",
    },
  },
  {
    q: { en: "How is a mosque's Qibla determined?", bn: "মসজিদের কিবলা কীভাবে নির্ণয় করা হয়?" },
    a: {
      en: "Surveyors traditionally use sun-shadow methods (e.g. Rashdul Qibla days when the sun is directly above the Kaaba) or precise compass + map-based bearing — same math we use here.",
      bn: "সার্ভেয়াররা সাধারণত সূর্যের ছায়া পদ্ধতি (যেমন রশিদুল কিবলা দিন, যখন সূর্য কাবার ঠিক উপরে থাকে) অথবা সঠিক কম্পাস + ম্যাপ ভিত্তিক বেয়ারিং ব্যবহার করেন — আমরাও একই গণিত ব্যবহার করি।",
    },
  },
  {
    q: { en: "Live compass shows wrong direction. Why?", bn: "লাইভ কম্পাস ভুল দিক দেখাচ্ছে কেন?" },
    a: {
      en: "Most often: the device needs calibration. Wave it in a figure-8 motion away from metal. iPhones require permission via a tap; some Android browsers don't expose absolute heading.",
      bn: "সাধারণত ক্যালিব্রেশন দরকার। ধাতব বস্তু থেকে দূরে গিয়ে 8-আকৃতিতে ঘুরিয়ে নিন। iPhone-এ ট্যাপ করে অনুমতি দিতে হয়; কিছু অ্যান্ড্রয়েড ব্রাউজার অ্যাবসোলিউট হেডিং দেয় না।",
    },
  },
  {
    q: { en: "Does it work while travelling abroad?", bn: "বিদেশে ভ্রমণে কাজ করবে?" },
    a: {
      en: "Yes — choose Auto-detect or enter coordinates manually anywhere on Earth and the bearing recalculates instantly.",
      bn: "হ্যাঁ — পৃথিবীর যেকোনো জায়গায় Auto-detect বা ম্যানুয়াল কোঅর্ডিনেট দিন, বেয়ারিং তাৎক্ষণিকভাবে পুনরায় হিসাব হবে।",
    },
  },
];

const SAVED_KEY = "qibla:saved-location";

type Mode = "auto" | "city" | "manual";

export const Route = createFileRoute("/qibla-direction")({
  head: () => {
    const seo = buildSeo({
      title: "কিবলা দিক নির্ণয় — Qibla Direction Finder BD",
      description:
        "বাংলাদেশের ৬৪ জেলা থেকে কিবলার দিক। GPS-ভিত্তিক, লাইভ কম্পাস। Qibla direction for all 64 districts of Bangladesh — true-north bearing, live magnetometer compass, distance to the Kaaba.",
      path: "/qibla-direction",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Qibla Direction", path: "/qibla-direction" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: QiblaPage,
});

function fmtNum(n: number, lang: "en" | "bn", digits = 0) {
  const s = n.toLocaleString("en-US", {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  });
  return lang === "bn" ? toBengaliNumerals(s) : s;
}

function QiblaPage() {
  const lang = useAppStore((s) => s.lang);
  const setLang = useAppStore((s) => s.setLang);
  const t = (k: Bilingual) => k[lang];

  // Force Bengali default once
  useEffect(() => {
    if (!useAppStore.getState().langTouched) setLang("bn");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [mode, setMode] = useState<Mode>("city");
  const [cityKey, setCityKey] = useState("dhaka");
  const [manualLat, setManualLat] = useState("23.8103");
  const [manualLon, setManualLon] = useState("90.4125");
  const [autoCoords, setAutoCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [geoStatus, setGeoStatus] = useState<"idle" | "loading" | "ok" | "denied">("idle");
  const [savedNote, setSavedNote] = useState(false);

  // Live compass
  const [liveOn, setLiveOn] = useState(false);
  const [heading, setHeading] = useState<number | null>(null);
  const [orientSupported, setOrientSupported] = useState(true);

  // Restore saved location
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem(SAVED_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { mode: Mode; cityKey?: string; lat?: string; lon?: string };
      if (parsed.mode) setMode(parsed.mode);
      if (parsed.cityKey) setCityKey(parsed.cityKey);
      if (parsed.lat) setManualLat(parsed.lat);
      if (parsed.lon) setManualLon(parsed.lon);
    } catch {
      /* ignore */
    }
  }, []);

  const coords = useMemo(() => {
    if (mode === "auto" && autoCoords) return autoCoords;
    if (mode === "manual") {
      const lat = parseFloat(manualLat);
      const lon = parseFloat(manualLon);
      if (Number.isFinite(lat) && Number.isFinite(lon)) return { lat, lon };
    }
    const c = BD_CITIES.find((c) => c.key === cityKey) ?? BD_CITIES[0];
    return { lat: c.lat, lon: c.lon };
  }, [mode, autoCoords, manualLat, manualLon, cityKey]);

  const result = useMemo(() => qiblaFor(coords.lat, coords.lon), [coords]);

  // Geolocation
  function detectLocation() {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setGeoStatus("denied");
      return;
    }
    setGeoStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setAutoCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        setGeoStatus("ok");
        setMode("auto");
      },
      () => setGeoStatus("denied"),
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 },
    );
  }

  // Device orientation (live compass)
  useEffect(() => {
    if (!liveOn || typeof window === "undefined") return;

    const handler = (e: DeviceOrientationEvent) => {
      // webkitCompassHeading: degrees from magnetic north (iOS)
      // alpha: 0 when device facing magnetic north (some browsers)
      const wk = (e as DeviceOrientationEvent & { webkitCompassHeading?: number }).webkitCompassHeading;
      let h: number | null = null;
      if (typeof wk === "number") {
        h = wk;
      } else if (typeof e.alpha === "number") {
        h = (360 - e.alpha) % 360;
      }
      if (h !== null) setHeading(h);
    };

    let cleanup = () => {};
    const start = async () => {
      try {
        const DOE = (window as unknown as {
          DeviceOrientationEvent?: { requestPermission?: () => Promise<"granted" | "denied"> };
        }).DeviceOrientationEvent;
        if (DOE && typeof DOE.requestPermission === "function") {
          const res = await DOE.requestPermission();
          if (res !== "granted") {
            setOrientSupported(false);
            setLiveOn(false);
            return;
          }
        }
        const evtName = "ondeviceorientationabsolute" in window ? "deviceorientationabsolute" : "deviceorientation";
        window.addEventListener(evtName, handler as EventListener, true);
        cleanup = () => window.removeEventListener(evtName, handler as EventListener, true);
      } catch {
        setOrientSupported(false);
        setLiveOn(false);
      }
    };
    void start();
    return () => cleanup();
  }, [liveOn]);

  function saveLocation() {
    if (typeof window === "undefined") return;
    localStorage.setItem(
      SAVED_KEY,
      JSON.stringify({ mode, cityKey, lat: manualLat, lon: manualLon }),
    );
    setSavedNote(true);
    window.setTimeout(() => setSavedNote(false), 1500);
  }

  // Compass arrow rotation:
  // Static mode: rotate arrow to bearing (with N at top of dial).
  // Live mode: dial rotates so N tracks magnetic north; arrow stays at bearing relative to dial.
  const dialRotation = liveOn && heading !== null ? -heading : 0;
  const arrowRotation = result.bearing;

  return (
    <CalculatorLayout
      slug="qibla-direction"
      title={t(STR.h1)}
      description={t(STR.desc)}
      iconName="Compass"
      faq={FAQ.map((f) => ({ q: t(f.q), a: t(f.a) }))}
    >
      <div className="space-y-6">
        {/* Mode selector */}
        <div>
          <label className={`mb-2 block text-sm font-medium ${lang === "bn" ? "font-bn" : ""}`}>
            {t(STR.inputMode)}
          </label>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {(
              [
                { id: "auto", label: STR.modeAuto, icon: Crosshair },
                { id: "city", label: STR.modeCity, icon: MapPin },
                { id: "manual", label: STR.modeManual, icon: Compass },
              ] as { id: Mode; label: Bilingual; icon: typeof Crosshair }[]
            ).map(({ id, label, icon: I }) => (
              <button
                key={id}
                type="button"
                onClick={() => setMode(id)}
                className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${
                  mode === id
                    ? "border-accent bg-accent/10 text-accent"
                    : "border-border bg-card hover:border-accent/40"
                } ${lang === "bn" ? "font-bn" : ""}`}
              >
                <I size={16} />
                {t(label)}
              </button>
            ))}
          </div>
        </div>

        {/* Per-mode inputs */}
        {mode === "auto" && (
          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <Button onClick={detectLocation} disabled={geoStatus === "loading"} className="gap-2">
              <Crosshair size={16} />
              {geoStatus === "loading" ? t(STR.detecting) : t(STR.detect)}
            </Button>
            {geoStatus === "ok" && autoCoords && (
              <p className={`mt-2 text-xs text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>
                {fmtNum(autoCoords.lat, lang, 4)}°, {fmtNum(autoCoords.lon, lang, 4)}°
              </p>
            )}
            {geoStatus === "denied" && (
              <p className={`mt-2 text-xs text-destructive ${lang === "bn" ? "font-bn" : ""}`}>
                {t(STR.permDenied)}
              </p>
            )}
          </div>
        )}

        {mode === "city" && (
          <div>
            <label className={`mb-2 block text-sm font-medium ${lang === "bn" ? "font-bn" : ""}`}>
              {t(STR.city)}
            </label>
            <select
              value={cityKey}
              onChange={(e) => setCityKey(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            >
              {BD_CITIES.map((c) => (
                <option key={c.key} value={c.key}>
                  {lang === "bn" ? c.bn : c.en} ({c.division})
                </option>
              ))}
            </select>
          </div>
        )}

        {mode === "manual" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`mb-1 block text-xs font-medium ${lang === "bn" ? "font-bn" : ""}`}>
                {t(STR.lat)}
              </label>
              <input
                type="number"
                step="0.0001"
                value={manualLat}
                onChange={(e) => setManualLat(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className={`mb-1 block text-xs font-medium ${lang === "bn" ? "font-bn" : ""}`}>
                {t(STR.lon)}
              </label>
              <input
                type="number"
                step="0.0001"
                value={manualLon}
                onChange={(e) => setManualLon(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>
        )}

        {/* Result + compass */}
        <div className="grid gap-6 md:grid-cols-2">
          <CompassDial
            dialRotation={dialRotation}
            arrowRotation={arrowRotation}
            lang={lang}
            live={liveOn}
          />

          <div className="space-y-3 rounded-xl border border-border bg-gradient-to-br from-accent/5 to-transparent p-5">
            <div>
              <div className={`text-xs uppercase tracking-wide text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>
                {t(STR.bearing)}
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-4xl font-bold tabular-nums text-accent">
                  {fmtNum(result.bearing, lang, 0)}°
                </span>
                <span className={`text-sm text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>
                  {result.cardinal[lang]}
                </span>
              </div>
            </div>

            <div>
              <div className={`text-xs uppercase tracking-wide text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>
                {t(STR.distance)}
              </div>
              <div className="mt-1 text-2xl font-semibold tabular-nums">
                ~{fmtNum(result.distanceKm, lang, 0)} {lang === "bn" ? "কিমি" : "km"}
              </div>
            </div>

            <p className={`text-sm text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>
              {t(STR.fromYourLocation)}{" "}
              <strong className="text-foreground">{result.cardinal[lang]}</strong>{" "}
              ({fmtNum(result.bearing, lang, 0)}°).
            </p>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button
                variant={liveOn ? "default" : "outline"}
                size="sm"
                onClick={() => setLiveOn((v) => !v)}
                className="gap-2"
              >
                <Smartphone size={14} />
                {liveOn ? t(STR.liveOn) : t(STR.liveOff)}
              </Button>
              <Button variant="outline" size="sm" onClick={saveLocation} className="gap-2">
                <RefreshCw size={14} />
                {savedNote ? t(STR.saved) : t(STR.saveLoc)}
              </Button>
            </div>

            {liveOn && orientSupported && (
              <p className={`text-xs text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>
                {t(STR.hint)}
              </p>
            )}
            {!orientSupported && (
              <p className={`text-xs text-amber-600 ${lang === "bn" ? "font-bn" : ""}`}>
                {t(STR.noOrient)}
              </p>
            )}
          </div>
        </div>

        {/* Precomputed table */}
        <section className="pt-4">
          <h2 className={`mb-3 text-lg font-bold ${lang === "bn" ? "font-bn" : ""}`}>
            {t(STR.precomputedTitle)}
          </h2>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left">
                <tr>
                  <th className={`px-3 py-2 ${lang === "bn" ? "font-bn" : ""}`}>
                    {lang === "bn" ? "জেলা" : "District"}
                  </th>
                  <th className={`px-3 py-2 ${lang === "bn" ? "font-bn" : ""}`}>
                    {lang === "bn" ? "বিভাগ" : "Division"}
                  </th>
                  <th className={`px-3 py-2 text-right ${lang === "bn" ? "font-bn" : ""}`}>
                    {lang === "bn" ? "বেয়ারিং" : "Bearing"}
                  </th>
                  <th className={`px-3 py-2 text-right ${lang === "bn" ? "font-bn" : ""}`}>
                    {lang === "bn" ? "দূরত্ব (কিমি)" : "Distance (km)"}
                  </th>
                </tr>
              </thead>
              <tbody>
                {BD_CITY_QIBLA.map(({ city, bearing, distanceKm }) => (
                  <tr key={city.key} className="border-t border-border/60">
                    <td className={`px-3 py-2 font-medium ${lang === "bn" ? "font-bn" : ""}`}>
                      {lang === "bn" ? city.bn : city.en}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{city.division}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {fmtNum(bearing, lang, 1)}°{" "}
                      <span className="text-xs text-muted-foreground">
                        {cardinalFromBearing(bearing).short[lang]}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {fmtNum(distanceKm, lang, 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Educational content */}
        <article className={`prose prose-sm max-w-none pt-4 dark:prose-invert ${lang === "bn" ? "font-bn" : ""}`}>
          <h2>{lang === "bn" ? "কিবলা কী?" : "What is Qibla?"}</h2>
          <p>
            {lang === "bn"
              ? "কিবলা হলো মক্কার মসজিদুল হারামে অবস্থিত পবিত্র কাবার দিক। প্রত্যেক মুসলিম পাঁচ ওয়াক্ত নামাজে কিবলামুখী হয়ে দাঁড়ান। কোরআনে আল্লাহ বলেন: ‘তুমি তোমার মুখ মাসজিদুল হারামের দিকে ফিরাও’ (সূরা বাকারা ১৪৪)।"
              : "The Qibla is the direction of the holy Kaaba inside Masjid al-Haram in Mecca. Muslims face it during the five daily prayers. The Quran instructs: ‘Turn your face toward al-Masjid al-Haram’ (Surah al-Baqarah 144)."}
          </p>
          <h2>{lang === "bn" ? "কিবলা কীভাবে নির্ণয় করা হয়?" : "How Qibla is calculated"}</h2>
          <p>
            {lang === "bn"
              ? `আমরা গ্রেট-সার্কেল ইনিশিয়াল বেয়ারিং ব্যবহার করি — পৃথিবীর গোলাকৃতির উপর আপনার অবস্থান (অক্ষাংশ φ₁, দ্রাঘিমাংশ λ₁) থেকে কাবার অবস্থান (φ₂ = ${fmtNum(KAABA_LAT, lang, 4)}°, λ₂ = ${fmtNum(KAABA_LON, lang, 4)}°) পর্যন্ত সবচেয়ে সংক্ষিপ্ত পথের প্রাথমিক দিক।`
              : `We use the great-circle initial bearing — the starting compass direction of the shortest path on the Earth's surface from your location (latitude φ₁, longitude λ₁) to the Kaaba (φ₂ = ${KAABA_LAT}°, λ₂ = ${KAABA_LON}°).`}
          </p>
          <pre className="overflow-x-auto whitespace-pre-wrap break-words text-xs">
{`y = sin(λ₂ − λ₁) · cos(φ₂)
x = cos(φ₁) · sin(φ₂) − sin(φ₁) · cos(φ₂) · cos(λ₂ − λ₁)
bearing = atan2(y, x)  // → degrees, 0°–360° from true north`}
          </pre>
          <h2>{lang === "bn" ? "বাংলাদেশ থেকে কিবলা" : "Qibla from Bangladesh"}</h2>
          <p>
            {lang === "bn"
              ? "বাংলাদেশের সকল জেলা থেকে কিবলা প্রায় পশ্চিম দিকে, সামান্য উত্তরে ঝোঁকা — ট্রু-নর্থ থেকে মোটামুটি ২৭৬° থেকে ২৭৯° এর মধ্যে।"
              : "From every district in Bangladesh the Qibla is roughly west, tilted slightly north — generally between 276° and 279° from true north."}
          </p>
          <h2>{lang === "bn" ? "মোবাইলে কিবলা ও কম্পাস ক্যালিব্রেশন" : "Mobile Qibla & compass calibration"}</h2>
          <ul>
            <li>
              {lang === "bn"
                ? "ফোনটি 8-আকৃতিতে কয়েকবার ঘুরিয়ে নিন (ম্যাগনেটোমিটার ক্যালিব্রেশন)।"
                : "Move the phone in a figure-8 motion to calibrate the magnetometer."}
            </li>
            <li>
              {lang === "bn"
                ? "ধাতব বস্তু, চুম্বক, ল্যাপটপ ও স্পিকার থেকে দূরে থাকুন।"
                : "Stay away from metal objects, magnets, laptops and speakers."}
            </li>
            <li>
              {lang === "bn"
                ? "iPhone-এ লাইভ কম্পাস চালু করতে অনুমতি দিতে হয় (এক ট্যাপ)।"
                : "On iPhone, enabling live compass requires a tap to grant motion permission."}
            </li>
            <li>
              {lang === "bn"
                ? "আমরা ট্রু-নর্থ বেয়ারিং দেখাই; বাংলাদেশে ম্যাগনেটিক ডিক্লিনেশন প্রায় ০–১°, পার্থক্য নগণ্য।"
                : "We show true-north bearing; magnetic declination in Bangladesh is ~0–1°, negligible for prayer."}
            </li>
          </ul>
        </article>

        {/* Cross-links */}
        <div className="grid gap-2 pt-2 sm:grid-cols-3">
          <Link to="/$slug" params={{ slug: "prayer-times" }} className="rounded-lg border border-border bg-card p-3 text-sm hover:border-accent">
            <div className={`font-semibold ${lang === "bn" ? "font-bn" : ""}`}>
              {lang === "bn" ? "নামাজের সময়" : "Prayer Times"}
            </div>
            <div className="text-xs text-muted-foreground">
              {lang === "bn" ? "৫ ওয়াক্তের সঠিক সময়" : "Five daily prayer times"}
            </div>
          </Link>
          <Link to="/$slug" params={{ slug: "sehri-iftar-time" }} className="rounded-lg border border-border bg-card p-3 text-sm hover:border-accent">
            <div className={`font-semibold ${lang === "bn" ? "font-bn" : ""}`}>
              {lang === "bn" ? "সেহরি ও ইফতার" : "Sehri & Iftar"}
            </div>
            <div className="text-xs text-muted-foreground">
              {lang === "bn" ? "রমাদান ক্যালেন্ডার" : "Ramadan calendar"}
            </div>
          </Link>
          <Link to="/$slug" params={{ slug: "hijri-date-converter" }} className="rounded-lg border border-border bg-card p-3 text-sm hover:border-accent">
            <div className={`font-semibold ${lang === "bn" ? "font-bn" : ""}`}>
              {lang === "bn" ? "হিজরি কনভার্টার" : "Hijri Converter"}
            </div>
            <div className="text-xs text-muted-foreground">
              {lang === "bn" ? "৩-উপায় কনভার্সন" : "Three-way conversion"}
            </div>
          </Link>
        </div>
      </div>
    </CalculatorLayout>
  );
}

function CompassDial({
  dialRotation,
  arrowRotation,
  lang,
  live,
}: {
  dialRotation: number;
  arrowRotation: number;
  lang: "en" | "bn";
  live: boolean;
}) {
  const cardinals = [
    { deg: 0, en: "N", bn: "উ", color: "text-red-600" },
    { deg: 90, en: "E", bn: "পূ", color: "text-foreground" },
    { deg: 180, en: "S", bn: "দ", color: "text-foreground" },
    { deg: 270, en: "W", bn: "প", color: "text-foreground" },
  ];

  return (
    <div className="flex items-center justify-center">
      <div className="relative aspect-square w-full max-w-[320px] rounded-full border-2 border-border bg-gradient-to-br from-muted/30 to-background shadow-inner">
        {/* Rotating dial (cardinal labels + tick marks) */}
        <div
          className="absolute inset-0 transition-transform duration-300 ease-out"
          style={{ transform: `rotate(${dialRotation}deg)` }}
        >
          {/* Tick marks */}
          {Array.from({ length: 36 }).map((_, i) => (
            <div
              key={i}
              className="absolute left-1/2 top-1 h-2 w-px -translate-x-1/2 bg-muted-foreground/40"
              style={{ transformOrigin: "50% calc(50vw)", transform: `translateX(-50%) rotate(${i * 10}deg) translateY(0)` }}
            />
          ))}
          {/* Cardinal labels */}
          {cardinals.map((c) => (
            <div
              key={c.deg}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              style={{ transform: `translate(-50%, -50%) rotate(${c.deg}deg) translateY(-42%) rotate(${-c.deg}deg)` }}
            >
              <span className={`text-sm font-bold ${c.color} ${lang === "bn" ? "font-bn" : ""}`}>
                {lang === "bn" ? c.bn : c.en}
              </span>
            </div>
          ))}

          {/* Qibla arrow (rotates with the dial in static mode; in live mode dial counter-rotates) */}
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transition-transform duration-500 ease-out"
            style={{
              width: 0,
              height: 0,
              transform: `translate(-50%, -50%) rotate(${arrowRotation}deg)`,
            }}
          >
            <div className="relative" style={{ width: 0, height: "120px" }}>
              {/* arrow shaft */}
              <div
                className="absolute left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-b from-red-500 to-red-700 shadow-lg"
                style={{ top: "-110px", width: "6px", height: "110px" }}
              />
              {/* arrow head */}
              <div
                className="absolute left-1/2 -translate-x-1/2"
                style={{
                  top: "-130px",
                  width: 0,
                  height: 0,
                  borderLeft: "10px solid transparent",
                  borderRight: "10px solid transparent",
                  borderBottom: "22px solid hsl(0 84% 50%)",
                }}
              />
              {/* tail counterweight */}
              <div
                className="absolute left-1/2 -translate-x-1/2 rounded-full bg-muted-foreground/60"
                style={{ top: "0", width: "4px", height: "30px" }}
              />
            </div>
          </div>
        </div>

        {/* Center pivot */}
        <div className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background bg-foreground shadow" />

        {/* Center label */}
        <div className="absolute left-1/2 top-[62%] -translate-x-1/2 text-center">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Qibla</div>
          <div className="text-xs font-semibold">🕋</div>
        </div>

        {/* Live badge */}
        {live && (
          <div className="absolute right-2 top-2 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold text-white">
            ● LIVE
          </div>
        )}
      </div>
    </div>
  );
}
